import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit2, Trash2, Upload, ExternalLink, Layout, X, Eye, EyeOff } from 'lucide-react';
import axiosInstance from '../../api/axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const TEMPLATE_OPTIONS = [
  { value: 'showcase', label: 'Showcase – große Hero-Bühne mit Akzentfarbe' },
  { value: 'minimal', label: 'Minimal – luftig, zentriert, ruhig' },
  { value: 'elegant', label: 'Elegant – dunkles Band, Serif-Schrift' },
];

const emptyForm = {
  slug: '', name: '', tagline: '', description: '',
  logo_url: '', hero_image: '',
  layout_template: 'showcase', accent_color: '#C4A084', is_active: true,
};

const BrandsManager = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null or brand object
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [importBrand, setImportBrand] = useState(null); // open csv import dialog

  useEffect(() => { fetchBrands(); }, []);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const r = await axiosInstance.get('/admin/brands');
      setBrands(r.data.brands || []);
    } catch (e) {
      toast.error('Marken konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (b) => { setEditing(b); setForm({ ...emptyForm, ...b }); setShowForm(true); };

  const save = async () => {
    if (!form.slug.trim() || !form.name.trim()) {
      toast.error('Slug und Name sind Pflichtfelder');
      return;
    }
    try {
      if (editing) {
        const { slug, ...patch } = form;
        await axiosInstance.put(`/admin/brands/${editing.id}`, patch);
        toast.success(`Marke „${form.name}" aktualisiert`);
      } else {
        await axiosInstance.post('/admin/brands', form);
        toast.success(`Marke „${form.name}" angelegt`);
      }
      setShowForm(false);
      await fetchBrands();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Speichern fehlgeschlagen';
      toast.error(msg);
    }
  };

  const remove = async (brand) => {
    if (!window.confirm(`Marke „${brand.name}" wirklich löschen? Die Produkte bleiben erhalten, verlieren aber den Marken-Tag.`)) return;
    try {
      await axiosInstance.delete(`/admin/brands/${brand.id}`);
      toast.success('Marke gelöscht');
      await fetchBrands();
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F5EDE4' }} data-testid="admin-brands-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-4 p-3 md:p-4 flex items-center justify-between" style={{ backgroundColor: '#C4A084' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded flex items-center justify-center bg-white/90">
              <Layout className="h-5 w-5" style={{ color: '#C4A084' }} />
            </div>
            <div>
              <h1 className="text-lg md:text-xl font-bold text-white">Marken-Welten</h1>
              <p className="text-xs md:text-sm text-white/80">Eigene Markenseiten unter <code className="bg-white/20 px-1 rounded">/marke/&lt;slug&gt;</code></p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={openNew} className="bg-white text-[#5D4E3C] hover:bg-[#FAF5EE] rounded-none" data-testid="admin-brand-new">
              <Plus className="h-4 w-4 mr-1" /> Neue Marke
            </Button>
            <Link to="/admin">
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" title="Zurück">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <p className="text-center py-12 text-[#8B7355]">Lädt …</p>
        ) : brands.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Layout className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-700 mb-2">Noch keine Marke angelegt.</p>
              <p className="text-sm text-gray-500 mb-6">Marken sind eine eigene Welt – ideal, wenn du eine zweite Produktlinie unter eigenem Namen führen willst.</p>
              <Button onClick={openNew} className="bg-[#C4A084] hover:bg-[#B89968] text-white rounded-none">
                <Plus className="h-4 w-4 mr-1" /> Erste Marke anlegen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="admin-brands-list">
            {brands.map((b) => (
              <Card key={b.id} className={`overflow-hidden ${b.is_active ? '' : 'opacity-60'}`} data-testid={`admin-brand-${b.slug}`}>
                <div className="h-16 w-full" style={{ backgroundColor: b.accent_color || '#C4A084' }} />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-[#3D3D3D] truncate">{b.name}</h3>
                        {b.is_active ? (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-800"><Eye className="h-3 w-3" /> Aktiv</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-gray-200 text-gray-700"><EyeOff className="h-3 w-3" /> Inaktiv</span>
                        )}
                      </div>
                      <p className="text-xs text-[#8B7355] mt-1">/marke/{b.slug}</p>
                      {b.tagline && <p className="text-sm italic text-[#5D4E3C] mt-1">{b.tagline}</p>}
                      <p className="text-xs text-[#5D4E3C] mt-2">
                        <strong>{b.product_count || 0}</strong> Produkt(e) · {TEMPLATE_OPTIONS.find(t => t.value === b.layout_template)?.label?.split('–')[0]?.trim() || 'Standard'}
                      </p>
                    </div>
                    {b.logo_url && (
                      <img src={b.logo_url} alt={b.name} className="h-12 w-12 object-contain flex-shrink-0 bg-[#FAF5EE] rounded" />
                    )}
                  </div>
                  <div className="flex gap-1 mt-3 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => openEdit(b)} className="rounded-none border-[#C4A084] text-[#5D4E3C]" data-testid={`admin-brand-edit-${b.slug}`}>
                      <Edit2 className="h-3.5 w-3.5 mr-1" /> Bearbeiten
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setImportBrand(b)} className="rounded-none border-[#C4A084] text-[#5D4E3C]" data-testid={`admin-brand-import-${b.slug}`}>
                      <Upload className="h-3.5 w-3.5 mr-1" /> CSV-Import
                    </Button>
                    <a href={`/marke/${b.slug}`} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="outline" className="rounded-none border-[#C4A084] text-[#5D4E3C]" data-testid={`admin-brand-view-${b.slug}`}>
                        <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ansehen
                      </Button>
                    </a>
                    <Button size="sm" variant="outline" onClick={() => remove(b)} className="rounded-none border-red-300 text-red-700 hover:bg-red-50" data-testid={`admin-brand-delete-${b.slug}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <BrandFormDialog
          form={form}
          setForm={setForm}
          isEdit={!!editing}
          onClose={() => setShowForm(false)}
          onSave={save}
        />
      )}
      {importBrand && (
        <CsvImportDialog brand={importBrand} onClose={() => setImportBrand(null)} onDone={fetchBrands} />
      )}
    </div>
  );
};

// ===== Form Dialog =====
const BrandFormDialog = ({ form, setForm, isEdit, onClose, onSave }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center sm:p-4">
    <div className="bg-white w-full sm:max-w-2xl max-h-[95vh] overflow-y-auto rounded-t-lg sm:rounded-lg" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} data-testid="brand-form-dialog">
      <div className="p-5 border-b border-[#E8DCC8] flex justify-between items-center sticky top-0 bg-white z-10">
        <h2 className="text-lg font-semibold text-[#3D3D3D]">{isEdit ? 'Marke bearbeiten' : 'Neue Marke anlegen'}</h2>
        <button onClick={onClose} aria-label="Schließen"><X className="h-5 w-5 text-[#8B7355]" /></button>
      </div>
      <div className="p-5 space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">URL-Slug *</Label>
            <Input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="z.B. lavendel-traum"
              disabled={isEdit}
              data-testid="brand-form-slug"
            />
            <p className="text-[11px] text-[#8B7355] mt-1">Aufruf später unter /marke/{form.slug || '<slug>'} – nach Anlegen nicht mehr änderbar.</p>
          </div>
          <div>
            <Label className="text-xs">Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Lavendel Traum" data-testid="brand-form-name" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Untertitel / Tagline</Label>
          <Input value={form.tagline || ''} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="Eine eigene Welt für besondere Stücke" />
        </div>
        <div>
          <Label className="text-xs">Beschreibung</Label>
          <Textarea rows={4} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Erzähle deinen Besucher:innen, was diese Marke einzigartig macht …" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Logo-URL</Label>
            <Input value={form.logo_url || ''} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://…" />
          </div>
          <div>
            <Label className="text-xs">Hero-Bild-URL</Label>
            <Input value={form.hero_image || ''} onChange={(e) => setForm({ ...form, hero_image: e.target.value })} placeholder="https://…" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Akzentfarbe</Label>
            <div className="flex gap-2 items-center">
              <input type="color" value={form.accent_color || '#C4A084'} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="h-10 w-14 cursor-pointer border" />
              <Input value={form.accent_color || ''} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} placeholder="#C4A084" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Layout-Vorlage</Label>
            <select
              value={form.layout_template}
              onChange={(e) => setForm({ ...form, layout_template: e.target.value })}
              className="h-10 w-full px-3 border border-[#D4C4B0] bg-white text-sm rounded"
              data-testid="brand-form-template"
            >
              {TEMPLATE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="accent-[#C4A084]" />
          Sichtbar (öffentliche Seite erreichbar)
        </label>
      </div>
      <div className="p-4 border-t border-[#E8DCC8] flex justify-end gap-2 sticky bottom-0 bg-white">
        <Button variant="outline" onClick={onClose} className="rounded-none border-[#D4C4B0]">Abbrechen</Button>
        <Button onClick={onSave} className="rounded-none bg-[#C4A084] hover:bg-[#B89968] text-white" data-testid="brand-form-save">{isEdit ? 'Speichern' : 'Anlegen'}</Button>
      </div>
    </div>
  </div>
);

// ===== CSV Import Dialog =====
const CsvImportDialog = ({ brand, onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleUpload = async () => {
    if (!file) { toast.error('Bitte CSV-Datei auswählen'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axiosInstance.post(`/admin/brands/${brand.id}/csv-import`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(r.data);
      toast.success(r.data.message || 'Import abgeschlossen');
      onDone?.();
    } catch (e) {
      toast.error('Import fehlgeschlagen: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csv = 'name,price,description,category,subcategory,stock,sku,unit,images\nLavendelseife,5.90,Handgesiedet mit Lavendelöl,Pflege,Seife,20,LAV-001,Stück,https://example.com/img.jpg\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `vorlage-${brand.slug}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white w-full sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-t-lg sm:rounded-lg" data-testid="brand-csv-dialog">
        <div className="p-5 border-b border-[#E8DCC8] flex justify-between items-center">
          <h2 className="text-lg font-semibold text-[#3D3D3D]">CSV-Import: {brand.name}</h2>
          <button onClick={onClose} aria-label="Schließen"><X className="h-5 w-5 text-[#8B7355]" /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-[#5D4E3C]">
            Lade eine CSV-Datei hoch, um mehrere Artikel auf einmal in die Marke „{brand.name}" zu importieren.
            Diese Artikel erscheinen NICHT im Haupt-Sortiment – nur auf <code className="bg-[#FAF5EE] px-1">/marke/{brand.slug}</code>.
          </p>
          <Button variant="outline" onClick={downloadTemplate} className="rounded-none border-[#C4A084] text-[#5D4E3C]" data-testid="brand-csv-template">
            <Upload className="h-4 w-4 mr-2 rotate-180" /> Vorlage-CSV herunterladen
          </Button>
          <div>
            <Label className="text-xs">CSV-Datei</Label>
            <Input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="brand-csv-file" />
            <p className="text-[11px] text-[#8B7355] mt-1">Spalten: name, price, description, category, subcategory, stock, sku, unit, images (Bilder mit ; trennen)</p>
          </div>
          {result && (
            <div className="bg-[#FAF5EE] p-3 border border-[#E8DCC8] text-sm" data-testid="brand-csv-result">
              <p className="font-semibold text-emerald-700">{result.imported} Artikel importiert</p>
              {result.failed_count > 0 && (
                <details className="mt-2">
                  <summary className="text-amber-700 cursor-pointer">{result.failed_count} Fehler – Details</summary>
                  <ul className="mt-1 text-xs text-amber-900 list-disc pl-5">
                    {result.failed_rows.map((r, i) => <li key={i}>Zeile {r.row}: {r.error}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
        <div className="p-4 border-t border-[#E8DCC8] flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-none border-[#D4C4B0]">{result ? 'Schließen' : 'Abbrechen'}</Button>
          {!result && (
            <Button onClick={handleUpload} disabled={uploading || !file} className="rounded-none bg-[#C4A084] hover:bg-[#B89968] text-white" data-testid="brand-csv-upload">
              <Upload className="h-4 w-4 mr-2" /> {uploading ? 'Lädt …' : 'Hochladen'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrandsManager;
